
import React, { useEffect, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Define orientation lock types
type OrientationLockType = 
  | "any"
  | "natural"
  | "landscape"
  | "portrait"
  | "portrait-primary"
  | "portrait-secondary"
  | "landscape-primary"
  | "landscape-secondary";

// Create a more accurate type for screen.orientation
interface ScreenOrientationExtended {
  lock(orientation: OrientationLockType): Promise<void>;
  unlock(): void;
  type: string;
  angle: number;
  onchange: ((this: ScreenOrientation, ev: Event) => any) | null;
  addEventListener<K extends keyof ScreenOrientationEventMap>(type: K, listener: (this: ScreenOrientation, ev: ScreenOrientationEventMap[K]) => any, options?: boolean | AddEventListenerOptions): void;
  addEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions): void;
  removeEventListener<K extends keyof ScreenOrientationEventMap>(type: K, listener: (this: ScreenOrientation, ev: ScreenOrientationEventMap[K]) => any, options?: boolean | EventListenerOptions): void;
  removeEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | EventListenerOptions): void;
  dispatchEvent(event: Event): boolean;
}

const OrientationPrompt = () => {
  const [isPortrait, setIsPortrait] = useState(false);
  const [permissionError, setPermissionError] = useState(false);
  
  // Check if we're in portrait mode
  useEffect(() => {
    const checkOrientation = () => {
      if (window.matchMedia("(orientation: portrait)").matches) {
        setIsPortrait(true);
      } else {
        setIsPortrait(false);
      }
    };
    
    // Check on mount
    checkOrientation();
    
    // Listen for orientation changes
    const mediaQuery = window.matchMedia("(orientation: portrait)");
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", checkOrientation);
    } else {
      // Fallback for older browsers
      window.addEventListener("resize", checkOrientation);
    }
    
    // Clean up
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", checkOrientation);
      } else {
        window.removeEventListener("resize", checkOrientation);
      }
    };
  }, []);

  const attemptRotation = () => {
    if ('orientation' in screen) {
      try {
        // Cast to our extended type that includes lock/unlock methods
        const orientation = screen.orientation as unknown as ScreenOrientationExtended;
        
        // Try to unlock orientation first (to prevent errors)
        if (typeof orientation.unlock === 'function') {
          orientation.unlock();
        }
        
        // Then lock to landscape
        if (typeof orientation.lock === 'function') {
          orientation.lock('landscape')
            .then(() => {
              console.log('Successfully locked to landscape');
              setIsPortrait(false);
              setPermissionError(false);
            })
            .catch(err => {
              console.warn('Failed to lock screen orientation:', err);
              setPermissionError(true);
            });
        } else {
          console.warn('Screen orientation lock API not available');
          setPermissionError(true);
        }
      } catch (error) {
        console.warn('Screen orientation API error:', error);
        setPermissionError(true);
      }
    } else {
      console.warn('Screen orientation API not supported');
      setPermissionError(true);
    }
  };

  // Only show the prompt if we're in portrait mode
  if (!isPortrait) {
    return null;
  }
  
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-6 max-w-sm text-center space-y-4 animate-fade-in">
        <RotateCcw className="w-12 h-12 mx-auto text-blue-500 animate-[spin_4s_linear_infinite]" />
        <h2 className="text-lg font-semibold">Please Rotate Your Device</h2>
        <p className="text-sm text-gray-600">
          For the best viewing experience, please rotate your device to landscape mode.
        </p>
        {permissionError && (
          <p className="text-xs text-amber-600">
            Unable to auto-rotate. Please manually rotate your device.
          </p>
        )}
        <Button 
          onClick={attemptRotation} 
          className="bg-blue-500 hover:bg-blue-600 text-white"
        >
          Rotate to Landscape
        </Button>
      </div>
    </div>
  );
};

export default OrientationPrompt;
